import * as _ from "lodash";
import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import AddMerchantRequest, { MerchantAdmin } from "./addMerchantRequest";
import FcmAccountEditor from "./general/FcmAccountEditor";
import MerchantAdminEditor from "./MerchantAdminEditor";
import AddressEditor from "./AddressEditor";
import Address from "domain/address";
import ErrorList from "common/ui/ErrorList";
import { PhoneRegex, UrlRegex } from "common/validation";
import Fcm from "domain/fcm";
import FcmAccount from "features/fcmAccounts/fcmAccount";
import NumberEditor from 'common/ui/NumberEditor';

export interface AddMerchantModalProps {
    fcms: Fcm[];
    onSave(request: AddMerchantRequest): void;
    onHide(): void;
    validationErrors: string[];
}


const AddMerchantModal: React.FC<AddMerchantModalProps> = props => {

    const [request, setRequest] = useState<AddMerchantRequest>({
        ...new AddMerchantRequest(),
        address: { line1: "", line2: "", city: "", region: "", country: "United States", postalCode: ""},
        admin: new MerchantAdmin(),
        additionalAdmins: [],
        fcmAccounts: [],
        giveUpFee: 1
    });
    const [admins, setAdmins] = useState<MerchantAdmin[]>([]);

    const [isValidated, setIsValidated] = useState(false);
    
    const formRef = React.createRef<HTMLFormElement>();

    function onSave() {
        if (formRef.current.checkValidity() && isValid()) {
            request.admin = admins[0];
            request.additionalAdmins = admins.slice(1);
            props.onSave(request);
        }
        setIsValidated(true);
    }

    function isValid(): boolean {
        return admins.length > 0;
    }

    function setAddress(address: Address){
        setRequest({...request, address: address})
    }

    function handleAddFcmAccount(account: string, fcmId: number) {
        const fcm = props.fcms.find(f => f.id === fcmId);
        const fcmAccount: FcmAccount = {
            id: -1,
            accountNumber: account,
            fcm: fcm
        };
        setRequest({...request, fcmAccounts: [...request.fcmAccounts, fcmAccount]});
    }

    function handleUpdateFcmAccount(account: string, fcmId: number) { 
        const fcmAccounts = request.fcmAccounts.map(m => m.accountNumber === account
            ? {...m, fcmId: fcmId }
            : m);
        setRequest({...request, fcmAccounts: fcmAccounts});
    }

    return <Modal show onHide={props.onHide} backdrop="static" size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    Add Merchant
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <Form validated={isValidated} ref={formRef} >
                        <Form.Group>
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={request.name}
                                onChange={(event: any) => setRequest({...request, name: event.target.value})}
                                required
                            />
                            <Form.Control.Feedback type="invalid">Merchant name is required.</Form.Control.Feedback>
                        </Form.Group>

                        <div className="row">
                            <div className="col">
                                <Form.Group>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={request.email}
                                        onChange={(event: any) => setRequest({...request, email: event.target.value?.trim()})}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">Valid email is required.</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="col">
                                <Form.Group>
                                    <Form.Label>Phone Number</Form.Label>
                                        <Form.Control
                                        type="text"
                                        name="phone"
                                        pattern={PhoneRegex.source}
                                        value={request.phone}
                                        onChange={(event: any) => setRequest({...request, phone: event.target.value?.trim()})}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">Valid phone number is required.</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <Form.Group>
                                    <Form.Label className="optional">Website URL</Form.Label>
                                        <Form.Control
                                        type="text"
                                        name="websiteUrl"
                                        pattern={UrlRegex.source}
                                        placeholder="https://"
                                        value={request.websiteUrl}
                                        onChange={(event: any) => setRequest({...request, websiteUrl: event.target.value?.trim()})}
                                    />
                                    <Form.Control.Feedback type="invalid">Invalid URL format (must start with http:// or https://)</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        </div>

                        <h4>Mailing Address</h4>
                        <AddressEditor
                            address={request.address}
                            setAddress={setAddress}
                        />
                    </Form>

                    <h4>FCM Accounts</h4>

                    <FcmAccountEditor 
                        accounts={request.fcmAccounts} 
                        fcms={props.fcms}
                        onAdd={handleAddFcmAccount}
                        onUpdate={handleUpdateFcmAccount}
                        onRemove={account => setRequest({...request, fcmAccounts: request.fcmAccounts.filter(m => m.accountNumber !== account)})}
                        showValidationErrors={isValidated}
                        />

                    <div className="row mt-3 mb-3">
                        <div className="col-md-2 mt-2">
                            <Form.Label>Give-Up Fee</Form.Label>
                        </div>
                        <div className="col-md-2">
                            <NumberEditor className="form-control"
                                required
                                allowDecimals
                                precision={4}
                                enforcePrecision
                                value={request.giveUpFee}
                                onValueChange={value => setRequest({ ...request, giveUpFee: value })}
                            />

                        </div>
                    </div>

                    <h4>Merchant Admins</h4>

                    <MerchantAdminEditor
                        admins={admins}
                        onAdd={admin => setAdmins([...admins, admin])}
                        onRemove={admin => setAdmins(admins.filter(m => m.email !== admin.email))}
                        showValidationErrors={isValidated}
                        />

                    <ErrorList errors={props.validationErrors} />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="pull-right">
                    <Button variant="secondary" className="ml-2" onClick={props.onHide}>
                        Cancel
                    </Button>&nbsp;
                    <Button variant="primary" onClick={onSave}>
                        Save
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
}

export default AddMerchantModal;